/**
 * Product grid buttons.
 * - [data-quick-add="<variant id>"] / [data-buy-now="<variant id>"]: products that fit every car
 *   and have no options add straight to the cart (or go to checkout) without leaving the page.
 * - [data-quick-buy="<product url>"]: products that need a choice first (car fit, installation,
 *   colour) open a popup with the same buy flow as the product page. Its body is the
 *   quick-buy section, fetched with the Section Rendering API and wired by
 *   product-buy-options.js (loaded on demand on pages that don't already have it).
 */
(function () {
  var buyOptionsSrc = document.currentScript && document.currentScript.getAttribute('data-buy-options-src');

  function productCount(cart) {
    // Installation lines ride along with their accessory, so they are not counted as items.
    return cart.items.reduce(function (n, item) {
      return item.properties && item.properties['For product'] ? n : n + item.quantity;
    }, 0);
  }

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var countEl = document.querySelector('[data-cart-count]');
        if (countEl) countEl.textContent = productCount(cart);
      });
  }

  // --- Toast ---
  var toastTimer;
  function toast(message, linkLabel, linkHref) {
    var el = document.querySelector('.cart-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cart-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.innerHTML = '';
    var text = document.createElement('span');
    text.textContent = message;
    el.appendChild(text);
    if (linkHref) {
      var link = document.createElement('a');
      link.className = 'cart-toast__link';
      link.href = linkHref;
      link.textContent = linkLabel;
      el.appendChild(link);
    }
    window.requestAnimationFrame(function () { el.classList.add('is-visible'); });
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { el.classList.remove('is-visible'); }, 3600);
  }

  /** Opens the slide-out cart when it's on; otherwise updates the count and shows a toast. */
  function afterAdd() {
    var drawer = window.CarzeloCartDrawer;
    var done = drawer ? drawer.refreshAfterAdd() : refreshCartCount().then(function () { return false; });
    return done.then(function (opened) {
      if (!opened) toast('Added to your cart', 'View cart', '/cart');
    });
  }
  window.CarzeloAfterAdd = afterAdd;

  // --- Quick-buy popup ---
  var modal, modalBody, lastFocus, buyOptionsPromise;

  function loadBuyOptions() {
    if (window.CarzeloBuyOptions) return Promise.resolve();
    if (!buyOptionsPromise) {
      buyOptionsPromise = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = buyOptionsSrc;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return buyOptionsPromise;
  }

  function buildModal() {
    modal = document.createElement('div');
    modal.className = 'quick-buy-modal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="quick-buy-modal__backdrop" data-quick-buy-close></div>' +
      '<div class="quick-buy-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="QuickBuyTitle" tabindex="-1">' +
        '<button type="button" class="quick-buy-modal__close" data-quick-buy-close aria-label="Close">&times;</button>' +
        '<div class="quick-buy-modal__body" data-quick-buy-body></div>' +
      '</div>';
    document.body.appendChild(modal);
    modalBody = modal.querySelector('[data-quick-buy-body]');
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-quick-buy-close]')) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && !modal.hidden) closeModal();
    });
  }

  function loadingMarkup() {
    return '<div class="quick-buy-modal__loading" aria-live="polite">' +
      '<div class="quick-buy-modal__skeleton quick-buy-modal__skeleton--head"></div>' +
      '<div class="quick-buy-modal__skeleton"></div>' +
      '<div class="quick-buy-modal__skeleton"></div>' +
      '<div class="quick-buy-modal__skeleton quick-buy-modal__skeleton--short"></div>' +
      '<span class="visually-hidden">Loading options…</span>' +
    '</div>';
  }

  function openModal(url, intent) {
    if (!modal) buildModal();
    lastFocus = document.activeElement;
    modalBody.innerHTML = loadingMarkup();
    modal.hidden = false;
    window.requestAnimationFrame(function () { modal.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
    modal.querySelector('.quick-buy-modal__dialog').focus();

    var sectionUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=quick-buy';
    Promise.all([
      fetch(sectionUrl, { headers: { Accept: 'text/html' } }).then(function (res) {
        if (!res.ok) throw new Error('Could not load product');
        return res.text();
      }),
      loadBuyOptions()
    ]).then(function (results) {
      var doc = new DOMParser().parseFromString(results[0], 'text/html');
      var root = doc.querySelector('[data-quick-buy-root]');
      if (!root) throw new Error('Could not load product');
      modalBody.innerHTML = '';
      modalBody.appendChild(document.importNode(root, true));
      var liveRoot = modalBody.querySelector('[data-quick-buy-root]');
      liveRoot.dataset.intent = intent;
      window.CarzeloBuyOptions.init(liveRoot, {
        onAdded: function () {
          closeModal();
          afterAdd();
        }
      });
    }).catch(function () {
      // Fall back to the full product page, which has the same options.
      window.location.href = url;
    });
  }

  function closeModal() {
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
    window.setTimeout(function () {
      if (!modal.classList.contains('is-open')) { modal.hidden = true; modalBody.innerHTML = ''; }
    }, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  document.addEventListener('click', function (event) {
    var quickBuyBtn = event.target.closest('[data-quick-buy]');
    if (quickBuyBtn) {
      event.preventDefault();
      openModal(quickBuyBtn.getAttribute('data-quick-buy'), quickBuyBtn.getAttribute('data-quick-buy-intent') || 'cart');
      return;
    }

    var buyBtn = event.target.closest('[data-buy-now]');
    if (buyBtn) {
      var buyVariant = parseInt(buyBtn.getAttribute('data-buy-now'), 10);
      // The product page / popup form's own Buy Now has no variant id; product-buy-options.js handles it.
      if (!buyVariant || !window.CarzeloCart) return;
      event.preventDefault();
      var buyText = buyBtn.textContent;
      buyBtn.disabled = true;
      buyBtn.textContent = 'Taking you to checkout...';
      window.CarzeloCart.addItems([{ id: buyVariant, quantity: 1 }]).then(function () {
        window.location.href = '/checkout';
      }).catch(function () {
        buyBtn.textContent = 'Try again';
        window.setTimeout(function () { buyBtn.textContent = buyText; buyBtn.disabled = false; }, 1500);
      });
      return;
    }

    var btn = event.target.closest('[data-quick-add]');
    if (!btn) return;
    event.preventDefault();
    var variantId = parseInt(btn.getAttribute('data-quick-add'), 10);
    if (!variantId || !window.CarzeloCart) return;

    var originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    window.CarzeloCart.addItems([{ id: variantId, quantity: 1 }]).then(afterAdd).then(function () {
      btn.textContent = 'Added ✓';
      window.setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);
    }).catch(function () {
      btn.textContent = 'Try again';
      window.setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);
    });
  });
})();
